import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Check, X } from "lucide-react";
import MerchantAuthLayout from "./MerchantAuthLayout";
import merchantApi from "../../lib/merchantApi";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "num", label: "Contains a number", test: (p) => /\d/.test(p) },
  { key: "sym", label: "Contains a symbol", test: (p) => /[!@#$%^&*()_+\-={}\[\]:;"'<>,.?/~`|\\]/.test(p) },
  { key: "nospace", label: "No spaces", test: (p) => p.length > 0 && !/\s/.test(p) },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const allPass = RULES.every((r) => r.test(password));
  const canSubmit = !!token && allPass && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      await merchantApi.post("/merchant/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset your password. Please try again.");
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <MerchantAuthLayout
        eyebrow="Reset password"
        title="Invalid reset link"
        subtitle="This link is missing its token. Please request a new password reset email."
        testId="reset-password-invalid"
      >
        <div className="text-center">
          <Link
            to="/login"
            data-testid="reset-back-to-login"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 transition-all px-5 py-3.5 text-[14px] font-medium"
          >
            Back to login
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </MerchantAuthLayout>
    );
  }

  if (done) {
    return (
      <MerchantAuthLayout
        eyebrow="Password updated"
        title="You're all set"
        subtitle="Your password has been changed. Sign in with your new credentials to continue."
        testId="reset-password-done"
      >
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            data-testid="reset-go-to-login"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 transition-all w-full px-5 py-3.5 text-[14px] font-medium"
          >
            Sign in
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </MerchantAuthLayout>
    );
  }

  return (
    <MerchantAuthLayout
      eyebrow="Reset password"
      title="Choose a new password"
      subtitle="Pick something strong — you'll use it every time you sign in."
      testId="reset-password-page"
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">New password</label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a strong password"
              autoComplete="new-password"
              required
              data-testid="reset-password-input"
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 pr-11 text-[14px] text-white placeholder:text-zinc-600"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <ul className="mt-3 grid grid-cols-2 gap-1.5">
            {RULES.map((r) => {
              const ok = r.test(password);
              return (
                <li
                  key={r.key}
                  className={`flex items-center gap-1.5 text-[11.5px] transition-colors ${
                    ok ? "text-emerald-300" : "text-zinc-500"
                  }`}
                >
                  {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  {r.label}
                </li>
              );
            })}
          </ul>
        </div>

        {error && (
          <div data-testid="reset-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="reset-submit-btn"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all w-full px-5 py-3.5 text-[14px] font-medium"
        >
          {submitting ? "Updating…" : "Update password"}
          {!submitting && <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />}
        </button>
      </form>
    </MerchantAuthLayout>
  );
}
